import { LitElement, html } from './-global.min.js'

class HHProductCardVariants extends LitElement {
  get anchor() {
    return this.querySelector('.js-anchor')
  }

  get variantButtons() {
    return [...this.querySelectorAll('.js-swatch')]
  }

  connectedCallback() {
    super.connectedCallback()
    this.variantButtons.forEach((button) => {
      button.addEventListener('click', this.onClickHandler.bind(this))
    })

    this._onCartUpdated = this._onCartUpdated.bind(this)
    document.addEventListener('cart:updated', this._onCartUpdated)

    this.productFormInput = this.closest('hh-product-form')?.querySelector(
      '.js-product-form-input'
    )

    if (window.location.href.includes('collections')) {
      this.initLinkClick()
    }

    this.gallery = this.querySelector('hh-swiper')
    this.secondSlideContent = this.gallery?.querySelector(
      '.swiper-slide:nth-child(2)'
    ).innerHTML

    const variantDataScript = this.querySelector('[type="application/json"]')
    if (variantDataScript) {
      this.productVariants = JSON.parse(variantDataScript.textContent)
      this.productCardId = Number.parseInt(variantDataScript.dataset.productId, 10)
    }

    if (this.gallery) {
      const resizeObserver = new ResizeObserver((entries) => {
        if (window.innerWidth >= 990) {
          this.removeSecondSlide()
        } else {
          this.addSecondSlide()
        }
      })

      resizeObserver.observe(this)
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback()
    this.variantButtons.forEach((button) => {
      button.removeEventListener('click', this.onClickHandler)
    })
    document.removeEventListener('cart:updated', this._onCartUpdated)
  }

  _isProductInCart(cart) {
    if (cart?.items?.length === 0 && cart?.items_removed === 0 && cart?.product_id === undefined) return null
    const items = cart?.items
    const itemsRemoved = cart?.items_removed || []
    const isItemRemoved = itemsRemoved.length !== 0
    const getData = (item, isVariantAdded = true) => ({ variantId: item.variant_id, quantity: item.quantity, isVariantAdded })
    if (isItemRemoved) {
      const productData = itemsRemoved.find((item) => item.product_id === this.productCardId)
      if (productData) return getData(productData, false)
      return null
    }
    if (items && !isItemRemoved) {
      const productData = items.find((item) => item.product_id === this.productCardId)
      if (productData) return getData(productData)
      return null
    }
    if (cart?.product_id === this.productCardId) {
      return getData(cart)
    }
   }

  _onCartUpdated(event) {
    const dataForUpdate = this._isProductInCart(event.detail)
    if (dataForUpdate) {
      this._updateVariantButtons(dataForUpdate)
    }
  }

  _getInventoryStock(variantId) {
    if (!this.productVariants) return null
    const variant = this.productVariants.find(
      (variant) => variant.id === Number.parseInt(variantId, 10)
    )
    return variant ? variant.inventory_quantity : null
  }

  _updateVariantButtons({ variantId: cartVariantId, quantity, isVariantAdded }) {
    this.variantButtons.forEach((button) => {
      const variantId = Number.parseInt(button.dataset.variantId, 10)
      const inventoryStock = this._getInventoryStock(variantId)
      if (variantId === cartVariantId) {
        if (isVariantAdded && inventoryStock <= quantity) {
          button.disabled = true
        }
        if (!isVariantAdded && inventoryStock >= quantity) {
          button.disabled = false
        }
      }
    })
  }

  onClickHandler(event) {
    if (this.anchor) {
      this.anchor.href = event.target.dataset.variantUrl
    }
    this.handleProductFormId(event)
  }

  handleProductFormId(event) {
    if (!this.productFormInput) return
    const variantId = event.target.dataset.variantId

    if (variantId) {
      this.productFormInput.value = variantId
    }
  }

  initLinkClick() {
    const link = this.querySelector('[data-product-card-link]')

    link?.addEventListener('click', (e) => {
      if (
        !e.target.closest('a') ||
        e.target.classList.contains('swiper-button')
      )
        return
      const linkHasPageReference = e.target.closest(`[data-page]`)
      if (!linkHasPageReference) return true
      e.preventDefault()
      const updatedURL = new URL(window.location.href)
      if (linkHasPageReference.hasAttribute('id')) {
        updatedURL.hash = linkHasPageReference.getAttribute('id')
      }
      updatedURL.searchParams.set('page', linkHasPageReference.dataset.page)
      history.pushState({}, '', updatedURL.toString())
      window.location.href = linkHasPageReference.getAttribute('href')
    })
  }

  addSecondSlide() {
    if (!this.secondSlideRemoved) return
    const newSlide = document.createElement('div')
    newSlide.classList.add('swiper-slide')
    newSlide.innerHTML = this.secondSlideContent
    this.gallery.swiper.addSlide(1, newSlide)
    this.secondSlideRemoved = false
  }

  removeSecondSlide() {
    if (this.secondSlideRemoved) return
    this.gallery.swiper.removeSlide(1)
    this.gallery.swiper.update()
    this.gallery.swiper.slideTo(0, 0)
    this.secondSlideRemoved = true
  }

  render() {
    return html`<slot></slot>`
  }
}

customElements.define('hh-product-card-variants', HHProductCardVariants)
